import React, { useState } from 'react';
import Navbar from '../navbar/Navbar';
import PartnerFaq from "../../../Assets/partner_fa.jpeg";
import Footer from "../../footer/Footer";
import { Helmet } from 'react-helmet-async';

interface FAQItem {
  question: string;
  answer: string;
}

interface ActiveIndices {
  general: number | null;
  profile: number | null;
  engagement: number | null;
  vetGroomer: number | null;
  creche: number | null;
  pharmacy: number | null;
}

const FAQPage: React.FC = () => {
  const [activeIndices, setActiveIndices] = useState<ActiveIndices>({
    general: null,
    profile: null,
    engagement: null,
    vetGroomer: null,
    creche: null,
    pharmacy: null
  });

  const toggleAnswer = (section: keyof ActiveIndices, index: number): void => {
    setActiveIndices(prev => ({
      ...prev,
      [section]: prev[section] === index ? null : index
    }));
  };

  const faqData1: FAQItem[] = [ // General FAQ
    {
      question: "What is the PupHub Partner App?",
      answer: "The PupHub Partner App is a platform designed for pet-related businesses such as vets, groomers, creches, and pharmacies to connect with the local pet-loving community. It allows you to manage your business profile, offer services, and engage with customers effectively."
    },
    {
      question: "Is the PupHub Partner App free to use?",
      answer: "Yes, the PupHub Partner App is free to download and use for all businesses. However, there may be premium features or services available for purchase."
    },
    {
      question: "How can I contact PupHub support?",
      answer: "You can contact our support team via email at support@puphub.com or reach us by phone at +918918080399. We're happy to assist you with any questions or issues."
    }
  ];

  const faqData2: FAQItem[] = [ // Setting up your Profile
    {
      question: "How do I create a business profile on the PupHub Partner App?",
      answer: "To create your profile, simply download the PupHub Partner App, sign up with your business details, and complete your profile by adding information about your services, location, contact details, and any special offers you may have."
    },
    {
      question: "How can I update my business information?",
      answer: "You can update your business profile at any time through the app. Simply log in, go to your profile section, and make changes to your business name, services, operating hours, and other relevant details."
    },
    {
      question: "Can I add multiple locations for my business?",
      answer: "Yes, you can add multiple locations for your business if you operate in different areas. Each location will have its own contact details and service offerings."
    }
  ];

  const faqData4: FAQItem[] = [ // Customer Engagement and Interaction
    {
      question: "How do I interact with customers on the PupHub Partner App?",
      answer: "You can communicate with customers directly through the in-app messaging feature. Respond to their inquiries, confirm appointments, and provide additional support as needed."
    },
    {
      question: "How can I promote my business to a wider audience?",
      answer: "You can increase your visibility by engaging with the community through the app, offering promotions, and encouraging satisfied customers to leave positive reviews. Additionally, you can leverage the app's advertising features to promote your business to pet owners nearby."
    }
  ];

  const faqData5: FAQItem[] = [ // Vet Clinics & Groomers
    {
      question: "How do I log in as a Vet Clinic or Groomer?",
      answer: "Vet clinics and groomers can log in using their specific partner credentials provided after registration. Once logged in, you will have access to manage your business profile, view appointments, and interact with customers."
    }
  ];

  const faqData6: FAQItem[] = [ // Creches Specific
    {
      question: "How do I log in as a Creche?",
      answer: "Creches can log in using their partner credentials. After logging in, you can manage your bookings, set available slots for services, and view all available services offered by vet clinics and groomers."
    },
    {
      question: "Can I book services from vet clinics or groomers?",
      answer: "Yes, creches can book services like veterinary appointments, grooming, and other treatments for the pets in their care. This allows you to offer comprehensive services to your clients."
    },
    {
      question: "Can I see pharmacy services?",
      answer: "Yes, creches can view and book pharmacy services through the app, ensuring that pets receive the medications they need while staying in your care."
    }
  ];

  const faqData7: FAQItem[] = [ // Pharmacy Viewing
    {
      question: "Can I access pharmacy services?",
      answer: "All business partners—vets, groomers, and creches—can view pharmacy services available to pets in their care. This allows you to recommend and use essential pet medications for your clients, whether they're in the clinic, during grooming, or staying at a creche."
    }
  ];

  const renderFAQSection = (section: keyof ActiveIndices, data: FAQItem[], title: string) => (
    <div className='mb-10 space-y-4'>
      <h2 className="text-center sm:text-justify text-2xl max-w-4xl text-white mt-10 m-5">
        {title}
      </h2>
      {data.map((faq, index) => (
        <div key={`${section}-${index}`} className="bg-white text-black rounded-lg shadow-md">
          <button
            onClick={() => toggleAnswer(section, index)}
            className="flex justify-between items-center w-full p-4 text-lg font-semibold focus:outline-none"
            aria-expanded={activeIndices[section] === index}
            aria-controls={`${section}-answer-${index}`}
          >
            {faq.question}
            <span className="text-gray-500">{activeIndices[section] === index ? '-' : '+'}</span>
          </button>
          {activeIndices[section] === index && (
            <div 
              id={`${section}-answer-${index}`}
              className="px-4 pb-4 text-gray-700"
              role="region"
            >
              {faq.answer}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://puphub.co.in/partner-faq" />
        <title>PupHub Partner | FAQ</title>
        <meta name="description" content="Frequently Asked Questions for PupHub Partners." />
      </Helmet>
      
      <Navbar />
      
      <div 
        className="w-[100vw] h-[30vh] sm:h-[80vh] relative" 
        style={{
          backgroundImage: `url(${PartnerFaq})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
        role="img"
        aria-label="PupHub Partner FAQ background"
      />
      
      <div className="flex justify-center">
        <p className="text-center sm:text-justify text-2xl max-w-4xl text-white mt-10 m-5">
          Welcome to our FAQ page for PupHub Partners! Here, you'll find answers to common questions about setting up your business profile, managing bookings, interacting with customers, and much more. Whether you're just getting started or need some tips on maximizing your app experience, we've got you covered!
        </p>
      </div>

      <div className="p-8 max-w-4xl mx-auto space-y-4">
        {renderFAQSection("general", faqData1, "General FAQs")}
        {renderFAQSection("profile", faqData2, "Setting Up Your Profile")}
        {renderFAQSection("engagement", faqData4, "Customer Engagement and Interaction")}
        {renderFAQSection("vetGroomer", faqData5, "Vet Clinics & Groomers")}
        {renderFAQSection("creche", faqData6, "Creches Specific")}
        {renderFAQSection("pharmacy", faqData7, "Pharmacy Viewing")}
      </div>

      <Footer />
    </>
  );
}

export default FAQPage;